import { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Flame, ArrowRight, Star } from "lucide-react";

export function HotLeadsBanner({ navigate }) {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);

  const fetchHotLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/hot-leads?limit=6`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error("Hot leads fetch error:", e); }
  }, [API]);

  useEffect(() => { fetchHotLeads(); }, [fetchHotLeads]);

  if (!data || data.total === 0) return null;

  return (
    <div data-testid="hot-leads-banner"
      className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/60 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
            <Flame className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-amber-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {data.total} Hot Lead{data.total > 1 ? "s" : ""} to act on
            </p>
            <p className="text-[11px] text-amber-700">
              High-priority prospects (score ≥ {data.threshold}) — strong reputation, weak digital presence. Pitch these first.
            </p>
          </div>
        </div>
        <Button data-testid="hot-leads-view-all" variant="outline" size="sm"
          onClick={() => navigate("/pipeline")}
          className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0">
          View pipeline <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {data.hot_leads.map((c) => (
          <button key={c.company_id}
            data-testid={`hot-lead-${c.company_id}`}
            onClick={() => navigate(`/companies/${c.company_id}`)}
            className="flex items-center justify-between gap-2 rounded-lg bg-white border border-amber-100 px-2.5 py-2 text-left hover:border-amber-300 hover:shadow-sm transition-all">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold truncate">{c.name}</p>
              <p className="text-[10px] text-muted-foreground truncate">
                {c.category}{(c.city || c.state) ? ` · ${[c.city, c.state].filter(Boolean).join(", ")}` : ""}
              </p>
              {c.google_rating ? (
                <span className="text-[10px] text-amber-700 flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{c.google_rating} · {c.google_reviews || 0} reviews
                </span>
              ) : null}
            </div>
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-[12px] font-bold">
              {c.priority_score}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
